import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageContainer, PageHeader, Skeleton } from "./dealerUi";

const FIELDS = [
  { key: "name", label: "Dealership name" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Contact email" },
  { key: "website", label: "Website" },
  { key: "address", label: "Street address" },
  { key: "city", label: "City" },
  { key: "state", label: "State", width: 24 },
  { key: "zip", label: "ZIP", width: 24 },
  { key: "hours", label: "Hours" },
  { key: "logo_url", label: "Logo URL" },
];

const DealerProfileEdit = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dealer/profile");
        setForm(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = FIELDS.reduce((acc, f) => {
        acc[f.key] = form[f.key] || null;
        return acc;
      }, {});
      payload.description = form.description || null;
      const { data } = await api.patch("/dealer/profile", payload);
      setForm(data);
      toast.success("Dealership profile updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <PageContainer>
        <PageHeader title="Dealership profile" subtitle="Loading…" />
        <Skeleton className="h-72" />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Dealership profile"
        subtitle="This information is shown to shoppers on your public dealership page."
        testId="dealer-profile-header"
        right={
          <Button
            className="bg-brand text-white hover:bg-brand-hover"
            onClick={save}
            disabled={saving}
            data-testid="dealer-profile-save-btn"
          >
            <Save size={15} /> {saving ? "Saving…" : "Save changes"}
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`field-${f.key}`}>{f.label}</Label>
                <Input
                  id={`field-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  data-testid={`dealer-profile-${f.key}`}
                />
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="field-description">Description</Label>
            <Textarea
              id="field-description"
              rows={4}
              value={form.description || ""}
              onChange={(e) => onChange("description", e.target.value)}
              data-testid="dealer-profile-description"
            />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
            Preview
          </p>
          {form.logo_url && (
            <div className="w-full h-32 rounded-xl overflow-hidden bg-slate-100 mb-3">
              <img src={form.logo_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <h4 className="font-display text-lg font-semibold text-slate-900">{form.name}</h4>
          <p className="text-sm text-slate-500 mt-1">
            {form.city}, {form.state} {form.zip}
          </p>
          <p className="text-xs text-slate-500 mt-2">{form.address}</p>
          <p className="text-xs text-slate-500 mt-2">{form.phone}</p>
          <p className="text-xs text-slate-500 mt-1">{form.email}</p>
          {form.hours && <p className="text-xs text-slate-500 mt-3 italic">{form.hours}</p>}
          {form.description && (
            <p className="text-sm text-slate-700 mt-4 leading-relaxed">{form.description}</p>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default DealerProfileEdit;
